import {
    registerApiInjection,
    getBaseApi,
    isStoreRegistered,
} from './storeRegistry';

/**
 * Shared endpoint builder type - no need to redefine in each entity.
 * This is a minimal structural type that matches RTK Query's EndpointBuilder.
 */
export interface EntityEndpointBuilder {
    query: <ResultType, ArgType>(definition: {
        query: (arg: ArgType) => string;
        transformResponse?: (
            response: unknown,
            meta?: unknown,
            arg?: ArgType
        ) => ResultType;
        providesTags: string[];
    }) => unknown;
    mutation: <ResultType, ArgType>(definition: {
        query: (arg: ArgType) => {
            url: string;
            method: string;
            body: unknown;
        };
        transformResponse?: (
            response: unknown,
            meta?: unknown,
            arg?: ArgType
        ) => ResultType;
        invalidatesTags: string[];
    }) => unknown;
}

/**
 * Creates an entity API instance that automatically injects endpoints.
 * Returns a proxy that forwards all property access to the injected API.
 *
 * @param config - Configuration with tagTypes and endpoint definitions
 * @returns A proxy object that behaves like the injected API instance
 *
 * @example
 * ```typescript
 * export const faqApi = createEntityApi({
 *   tagTypes: ['FAQS'],
 *   endpoints: faqEndpoints,
 * });
 *
 * export const { useGetFaqsQuery, usePostFaqMutation } = faqApi;
 * ```
 */
export function createEntityApi(config: {
    tagTypes: string[];
    endpoints: (builder: EntityEndpointBuilder) => Record<string, unknown>;
}) {
    type BaseApi = ReturnType<typeof getBaseApi>;
    let injectedApi: BaseApi | null = null;

    const ensureInjected = (): BaseApi => {
        if (injectedApi) {
            return injectedApi;
        }

        if (!isStoreRegistered()) {
            throw new Error(
                'Store not registered. Entity API cannot be used before store initialization.'
            );
        }

        const baseApi = getBaseApi();
        injectedApi = (
            baseApi as {
                enhanceEndpoints: (options: { addTagTypes: string[] }) => {
                    injectEndpoints: (options: {
                        endpoints: (builder: unknown) => unknown;
                    }) => BaseApi;
                };
            }
        )
            .enhanceEndpoints({ addTagTypes: config.tagTypes })
            .injectEndpoints({
                endpoints: (builder: unknown) =>
                    config.endpoints(builder as EntityEndpointBuilder),
            });

        return injectedApi;
    };

    registerApiInjection((api) => {
        injectedApi = (
            api as {
                enhanceEndpoints: (options: { addTagTypes: string[] }) => {
                    injectEndpoints: (options: {
                        endpoints: (builder: unknown) => unknown;
                    }) => BaseApi;
                };
            }
        )
            .enhanceEndpoints({ addTagTypes: config.tagTypes })
            .injectEndpoints({
                endpoints: (builder: unknown) =>
                    config.endpoints(builder as EntityEndpointBuilder),
            });
    });

    if (isStoreRegistered()) {
        ensureInjected();
    }

    // Return proxy that forwards all property access to injected API
    // Using 'any' for the proxy target because hooks are dynamically added by RTK Query
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return new Proxy({} as any, {
        get(_target, prop: string | symbol) {
            const api = ensureInjected();
            return api[prop as string];
        },
    });
}
